import React from "react";
import PropTypes from "prop-types";
import { useField } from "formik";
import { format } from "date-fns";
import TextField from "@mui/material/TextField";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import FormHelperText from "@mui/material/FormHelperText";
import Typography from "@mui/material/Typography";
import FormInputShell from "./FormInputShell";
import { InputShell } from "./FormInputShell";
import dayjs from "dayjs";

interface SimpleDateInputProps {
  name: string;
  value: any;
  readOnly?: boolean;
  onChange: (date: any) => void;
  removeBottomMargin?: boolean;
  customStyle?: any;
  label?: string;
  [key: string]: any;
}

const SimpleDateInput = ({
  name,
  value,
  readOnly,
  onChange,
  removeBottomMargin,
  customStyle,
  ...props
}: SimpleDateInputProps) => {
  return (
    <InputShell name={name} label={props.label} removeBottomMargin>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        {readOnly ? (
          <Typography variant="body1" gutterBottom>
            {format(new Date(value), "dd MM, yyyy")}
          </Typography>
        ) : (
          <DatePicker
            value={dayjs(value)}
            format="DD/MM/YYYY"
            maxDate={dayjs()}
            // views={["day", "month", "year"]}
            readOnly={readOnly}
            disabled={readOnly}
            onChange={onChange}
            slotProps={{
              textField: {
                size: "small",
                style: {
                  width: "100%",
                  height: 38,
                  ...customStyle,
                },
                InputProps: {
                  style: {
                    height: 38,
                    width: "100%",
                    ...customStyle,
                  },
                },
              },
            }}
            {...props}
            label={""}
          />
        )}
      </LocalizationProvider>
    </InputShell>
  );
};

SimpleDateInput.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.any.isRequired,
  readOnly: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  removeBottomMargin: PropTypes.bool,
  customStyle: PropTypes.object,
};

const DateInput = ({ label, readOnly, ...props }: any) => {
  const [field, meta, helper] = useField(props);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      {readOnly ? (
        <Typography variant="body1" gutterBottom>
          {format(new Date(field.value), "dd MMM, yyyy")}
        </Typography>
      ) : (
        <DatePicker
          autoOk
          fullWidth
          mask=""
          variant="inline"
          error={meta.touched && !!meta.error}
          inputVariant="outlined"
          inputFormat="dd/MM/yyyy"
          readOnly={readOnly}
          disabled={readOnly}
          renderInput={(props) => (
            <TextField {...props} style={{ width: "100%" }} />
          )}
          InputProps={{
            style: {
              height: 38,
              width: "100%",
            },
          }}
          onClose={() => helper.setTouched(true)}
          {...field}
          {...props}
          onChange={(date) => {
            helper.setValue(date);
          }}
        />
      )}
      <FormHelperText error={meta.touched && !!meta.error}>
        {meta.touched && meta.error}
      </FormHelperText>
    </LocalizationProvider>
  );
};

const FormDateInput = (props: any) => (
  <FormInputShell label={props.label} name={props.name}>
    <DateInput {...props} />
  </FormInputShell>
);

FormDateInput.propTypes = DatePicker.propTypes;

export default FormDateInput;
export { DateInput, SimpleDateInput };
